/**
 * The main screen: where the driver is, their status, clocking on and off and pausing,
 * the job they're on and what's coming up. Offers pop up over it (see offer.tsx).
 */
import { errorMessage } from "@taxi/mobile-web/api";
import { miles } from "@taxi/mobile-web/format";
import { useHeight, useNow } from "@taxi/mobile-web/hooks";
import {
  BellIcon,
  ChevronRight,
  ClockIcon,
  LocateIcon,
  PauseIcon,
  PlayIcon,
  PowerIcon,
} from "@taxi/mobile-web/icons";
import { MapView } from "@taxi/mobile-web/map";
import { usePush } from "@taxi/mobile-web/push";
import { Link, navigate } from "@taxi/mobile-web/router";
import { Button, Card, Notice, Plate, useToast } from "@taxi/mobile-web/ui";
import { type ReactNode, useState } from "react";
import { pickupWhen, placeParts, StatusChip, TripRoute } from "../components/parts";
import { unlockSound, useSoundReady } from "../lib/device";
import { type ShiftAction, useDriver } from "../lib/driver";
import { metresBetween, type Sharing } from "../lib/location";
import { useSession } from "../lib/session";
import type { DriverJob, LatLng } from "../lib/types";

/** Asks for the location from a tap, which is when phones are happiest to ask. */
function askForLocation(): Promise<void> {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve();
    navigator.geolocation.getCurrentPosition(
      () => resolve(),
      () => resolve(),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  });
}

export const JOB_STEP: Partial<Record<DriverJob["status"], string>> = {
  accepted: "Head to the pickup",
  arrived: "At the pickup",
  on_board: "On the way to the destination",
};

export function DriveScreen() {
  const { me, jobs, shift, location } = useDriver();
  const toast = useToast();
  const [busy, setBusy] = useState<ShiftAction | null>(null);
  const [sheet, sheetHeight] = useHeight<HTMLDivElement>();
  const status = me.live.status;
  const current = jobs?.current ?? null;
  const fix = location.fix;

  async function act(action: ShiftAction) {
    setBusy(action);
    try {
      if (action === "clock-on") await askForLocation();
      await shift(action);
      if (action === "clock-on") toast("You're on. Keep the app open on screen to get jobs.");
    } catch (error) {
      toast(errorMessage(error), "error");
    } finally {
      setBusy(null);
    }
  }

  let panel: ReactNode;
  if (status === "off") {
    panel = <OffShift busy={busy} onClockOn={() => act("clock-on")} />;
  } else if (current) {
    panel = <CurrentJob job={current} from={fix} />;
  } else if (status === "paused") {
    panel = (
      <>
        <div className="drive-heading">
          <span className="overline">On a break</span>
          <h2>You're paused</h2>
          <p>No new jobs until you resume.</p>
        </div>
        <Button className="btn-hero" loading={busy === "resume"} onClick={() => act("resume")}>
          <PlayIcon size={20} /> Resume
        </Button>
        <Button variant="ghost" loading={busy === "clock-off"} onClick={() => act("clock-off")}>
          Clock off
        </Button>
      </>
    );
  } else {
    panel = (
      <>
        <div className="drive-heading">
          <span className="overline drive-online">
            <i aria-hidden="true" /> You're on
          </span>
          <h2>Waiting for a job</h2>
          <p>Jobs go to the nearest free driver. Keep the app open on screen.</p>
        </div>
        <div className="drive-actions">
          <Button variant="secondary" loading={busy === "pause"} onClick={() => act("pause")}>
            <PauseIcon size={18} /> Pause
          </Button>
          <Button
            variant="secondary"
            className="danger-text"
            loading={busy === "clock-off"}
            onClick={() => act("clock-off")}
          >
            <PowerIcon size={18} /> Clock off
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className={`drive drive-${status}`}>
      <MapView
        className="drive-map"
        driver={fix ? { lat: fix.lat, lng: fix.lng, heading: fix.heading } : null}
        carLabel="You"
        pickup={current && current.status !== "on_board" ? current.pickup : null}
        dropoff={current?.status === "on_board" ? current.dropoff : null}
        bottomInset={sheetHeight || 320}
      />
      <div className="drive-top">
        <StatusChip status={status} />
      </div>
      <div className="drive-sheet" ref={sheet}>
        <div className="sheet-grabber" aria-hidden="true" />
        {panel}
        {status !== "off" && <LocationLine location={location} />}
        {status !== "off" && <SoundLine />}
        {status !== "off" && <PushLine />}
        <Upcoming queue={jobs?.queue ?? []} />
      </div>
    </div>
  );
}

function OffShift({ busy, onClockOn }: { busy: ShiftAction | null; onClockOn: () => void }) {
  const { me } = useDriver();
  const expiring = me.documents.filter((d) => d.status === "expiring");
  const vehicle = me.profile.vehicle;
  return (
    <>
      <div className="drive-heading">
        <span className="overline">Off shift</span>
        <h2>Hello, {me.profile.name.split(" ")[0]}</h2>
        <p>Clock on to start getting jobs.</p>
      </div>
      {vehicle && (
        <div className="drive-vehicle">
          <Plate registration={vehicle.registration} />
          <span>
            {vehicle.colour} {vehicle.make} {vehicle.model}
          </span>
        </div>
      )}
      {!me.canClockOn && me.clockOnBlockedReason && (
        <Notice tone="error">{me.clockOnBlockedReason}</Notice>
      )}
      {expiring.map((doc) => (
        <Notice key={doc.kind} tone="warning">
          Your {doc.label.toLowerCase()} expires
          {doc.daysLeft === 0
            ? " today"
            : ` in ${doc.daysLeft} day${doc.daysLeft === 1 ? "" : "s"}`}
          . Send the office the new one.
        </Notice>
      ))}
      <Button
        className="btn-hero"
        loading={busy === "clock-on"}
        disabled={!me.canClockOn}
        onClick={onClockOn}
      >
        <PowerIcon size={22} /> Clock on
      </Button>
    </>
  );
}

function CurrentJob({ job, from }: { job: DriverJob; from: LatLng | null }) {
  const target = job.status === "on_board" ? job.dropoff : job.pickup;
  const away = from ? miles(metresBetween(from, target)) : null;
  return (
    <Card className="current-job">
      <div className="current-job-head">
        <span className="overline">{JOB_STEP[job.status] ?? "Your job"}</span>
        {away && <span className="pill pill-primary">{away} away</span>}
      </div>
      <strong className="current-job-place">{placeParts(target).title}</strong>
      <TripRoute
        pickup={job.pickup}
        stops={job.status === "on_board" ? job.stops : []}
        dropoff={job.dropoff}
      />
      <Button onClick={() => navigate(`/jobs/${job.id}`)} value={`Job ${job.number}`}>
        Open job
      </Button>
    </Card>
  );
}

function LocationLine({ location }: { location: Sharing & { retry: () => void } }) {
  const now = useNow(5_000);
  if (location.status === "denied") {
    return (
      <Notice
        tone="error"
        action={
          <Button block={false} variant="secondary" onClick={location.retry}>
            Try again
          </Button>
        }
      >
        Location is off for this app, so jobs can't reach you. Allow it in your browser's settings
        for this site.
      </Notice>
    );
  }
  if (location.status === "unavailable") {
    return (
      <Notice
        tone="warning"
        action={
          <Button block={false} variant="secondary" onClick={location.retry}>
            Try again
          </Button>
        }
      >
        Can't find where you are. Check the phone's location is on.
      </Notice>
    );
  }
  const quiet = location.sentAt !== null && now.getTime() - location.sentAt > 90_000;
  return (
    <p className={`location-line${quiet ? " location-quiet" : ""}`}>
      <LocateIcon size={16} />
      {location.status === "starting"
        ? "Finding where you are…"
        : quiet
          ? "Location not sent for a while: check your signal"
          : "Sharing your location while you're on shift"}
    </p>
  );
}

function SoundLine() {
  const ready = useSoundReady();
  if (ready) return null;
  return (
    <button type="button" className="sound-hint" onClick={unlockSound}>
      <BellIcon size={18} />
      <span>
        <strong>Turn on the offer sound</strong>
        Tap here so new jobs chime
      </span>
    </button>
  );
}

/** Not asked again until the app's next opened. */
let pushCardDismissed = false;

/** Asks for notifications, so offers reach a driver who's switched apps for a moment. */
function PushLine() {
  const { firm } = useSession();
  const push = usePush("driver", firm.webPush.publicKey);
  const toast = useToast();
  const [dismissed, setDismissed] = useState(pushCardDismissed);
  if (dismissed || !push.available || push.support !== "supported" || push.state !== "off")
    return null;
  async function turnOn() {
    try {
      if ((await push.toggle()) === "blocked")
        toast("Notifications are blocked for this site in your browser settings.");
    } catch (error) {
      toast(errorMessage(error), "error");
    }
  }
  return (
    <Card className="push-card">
      <BellIcon />
      <div>
        <strong>Turn on notifications</strong>
        <span>So offers reach you if you switch to another app for a moment.</span>
      </div>
      <div className="push-card-actions">
        <Button block={false} variant="secondary" onClick={turnOn}>
          Turn on
        </Button>
        <Button
          block={false}
          variant="ghost"
          onClick={() => {
            pushCardDismissed = true;
            setDismissed(true);
          }}
        >
          Not now
        </Button>
      </div>
    </Card>
  );
}

function Upcoming({ queue }: { queue: DriverJob[] }) {
  if (queue.length === 0) return null;
  return (
    <section className="upcoming">
      <h3 className="section-title">Coming up</h3>
      {queue.slice(0, 3).map((job) => (
        <Link key={job.id} to={`/jobs/${job.id}`} className="upcoming-job">
          <ClockIcon size={18} />
          <span>
            <strong>{pickupWhen(job)}</strong>
            <span>from {placeParts(job.pickup).title}</span>
          </span>
          <ChevronRight size={18} />
        </Link>
      ))}
      {queue.length > 3 && (
        <Link to="/jobs" className="text-link">
          And {queue.length - 3} more
        </Link>
      )}
    </section>
  );
}
